import React, { useCallback, useMemo, useState } from 'react';
import { ConfirmationDialog } from './ConfirmationDialog';
import type { ConfirmationDialogProps } from './ConfirmationDialog';

type ConfirmationDialogRequest = Pick<
  ConfirmationDialogProps,
  | 'title'
  | 'message'
  | 'cancelLabel'
  | 'confirmLabel'
  | 'confirmColor'
  | 'destructive'
  | 'cancelAriaLabel'
  | 'confirmAriaLabel'
  | 'detailsLabel'
  | 'details'
  | 'aria-labelledby'
>;

type PendingConfirmationDialog = ConfirmationDialogRequest & {
  resolve: (confirmed: boolean) => void;
};

export function useConfirmationDialog(): {
  requestConfirmation: (request: ConfirmationDialogRequest) => Promise<boolean>;
  confirmDestructive: (
    title: string,
    message: React.ReactNode,
    confirmLabel?: string,
    cancelLabel?: string
  ) => Promise<boolean>;
  cancelPendingConfirmation: () => void;
  confirmationDialog: React.ReactNode;
} {
  const [pending, setPending] = useState<PendingConfirmationDialog | null>(null);

  const requestConfirmation = useCallback((request: ConfirmationDialogRequest) => (
    new Promise<boolean>((resolve) => {
      setPending({ ...request, resolve });
    })
  ), []);

  const confirmDestructive = useCallback((
    title: string,
    message: React.ReactNode,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel'
  ) => requestConfirmation({ title, message, cancelLabel, confirmLabel, destructive: true }), [requestConfirmation]);

  const resolvePending = useCallback((confirmed: boolean) => {
    setPending((current) => {
      current?.resolve(confirmed);
      return null;
    });
  }, []);

  const cancelPendingConfirmation = useCallback(() => {
    resolvePending(false);
  }, [resolvePending]);

  const confirmationDialog = useMemo(() => (
    <ConfirmationDialog
      open={Boolean(pending)}
      title={pending?.title ?? ''}
      message={pending?.message ?? ''}
      cancelLabel={pending?.cancelLabel}
      confirmLabel={pending?.confirmLabel}
      confirmColor={pending?.confirmColor}
      destructive={pending?.destructive}
      cancelAriaLabel={pending?.cancelAriaLabel}
      confirmAriaLabel={pending?.confirmAriaLabel}
      detailsLabel={pending?.detailsLabel}
      details={pending?.details}
      onCancel={() => resolvePending(false)}
      onConfirm={() => resolvePending(true)}
      aria-labelledby={pending?.['aria-labelledby']}
    />
  ), [pending, resolvePending]);

  return {
    requestConfirmation,
    confirmDestructive,
    cancelPendingConfirmation,
    confirmationDialog,
  };
}
